<template>
<span v-if="$can(this.permissions.ROUTING.GENERATE_SHUTTLE_FILE)">
  <el-button
      id="btn-generate-shuttle-file"
      type="success"
      plain
      size="mini"
      @click="dialogVisible = true"
    >{{ $t('routing.shuttleSetUp.generateShuttleFile') }}</el-button>
  <el-dialog
    :close-on-click-modal="false"
    v-on:open="handleOpen"
    :title="$t('routing.shuttleSetUp.generateShuttleFile')"
    :visible.sync="dialogVisible"
    width="40%"
    id="generate-shuttle-file-dialog"
    class="dialog-with-cols"
    :before-close="handleClose">
    <div id="dialog-body">
      <el-form :model="form" :rules="formRules" ref="form" label-position="top" size="mini" class="form-with-cols">
        <el-col :span="24">
            <el-col :md="24" :sm="24" :xs="24" class="form-vertical-item">
                <el-form-item :label="$t('routing.shuttleSetUp.warehouse')" prop="warehouse">
                  <el-input
                  v-model="form.warehouse"
                  disabled
                  class="inline-input"
                  id="form-warehouse"
                  ></el-input>
              </el-form-item>
            </el-col>
            <el-col :md="12" :sm="24" :xs="24" class="form-vertical-item">
                <el-form-item :label="$t('routing.shuttleSetUp.dateFrom')" prop="dateFrom">
                    <el-date-picker
                    v-model="form.dateFrom"
                    style="width: 100%"
                    format="MM/dd/yyyy"
                    :picker-options="shuttleSetUp.datePickerOptions"
                    type="date"
                    id="form-date-from">
                    </el-date-picker>
                </el-form-item>
            </el-col>
            <el-col :md="12" :sm="24" :xs="24" class="form-vertical-item">
                <el-form-item :label="$t('routing.shuttleSetUp.dateTo')" prop="dateTo">
                    <el-date-picker
                    v-model="form.dateTo"
                    style="width: 100%"
                    format="MM/dd/yyyy"
                    :picker-options="shuttleSetUp.datePickerOptions"
                    type="date"
                    id="form-date-to">
                    </el-date-picker>
                </el-form-item>
            </el-col>
            </el-col>
        </el-form>
    </div>
    <div slot="footer" class="dialog-footer">
       <el-button @click="dialogVisible = false" size="mini">{{ $t('common.cancel') }}</el-button>
       <el-button type="primary" @click="handleSubmit" size="mini" class="margin-r-18" id="btn-submit-extension">{{ $t('routing.shuttleSetUp.generateShuttleFile') }}</el-button>
    </div>
  </el-dialog>
</span>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { Message } from 'element-ui';
import moment from 'moment';
import constants from '@/utils/constants';
import shuttleFileService from '@/api/shuttleFileService';
import {
  SHUTTLE_FILE_COUNT_BY_DATE,
  SHUTTLE_FILE_COUNT_UNITS_TO_GENERATE,
  SHUTTLE_FILE_GENERATE,
  SHUTTLE_FILE_SEARCH
} from '@/store/actions';

export default {
  components: {},
  computed: {
    ...mapGetters([
      'shuttleSetUp',
      'shuttleFile',
      'user'
    ]),
    receivingDate() {
      return moment(this.shuttleFile.actualFilters.receivingDate.getTime()).isSameOrBefore(Date.now(), 'day') ? null : this.shuttleFile.actualFilters.receivingDate;
    }
  },
  data() {
    const validateDateFrom = (rule, value, callback) => {
      if (value == null || value === '') {
        callback(new Error(this.$t('routing.shuttleSetUp.error.dateFromRequired')));
      } else {
        if (this.form.dateTo != null && this.form.dateTo !== '') {
          this.$refs.form.validateField('dateTo');
        }
        callback();
      }
    };
    const validateDateTo = (rule, value, callback) => {
      if (value == null || value === '') {
        callback(new Error(this.$t('routing.shuttleSetUp.error.dateToRequired')));
      } else if (this.form.dateFrom != null && this.form.dateFrom !== '') {
        const momentFrom = moment(this.form.dateFrom.getTime());
        const momentTo = moment(value.getTime());
        const differenceInDays = momentTo.diff(momentFrom, 'days');
        if (differenceInDays < 0) {
          callback(new Error(this.$t('routing.shuttleSetUp.error.dateToMustBeGreater')));
        } else if (differenceInDays >= this.maxDays) {
          let message = this.$t('routing.shuttleSetUp.error.maximumDays');
          message = message.replace('[days]', this.maxDays);
          callback(new Error(message));
        } else {
          callback();
        }
      } else {
        callback();
      }
    };
    return {
      dialogVisible: false,
      form: {
        dateFrom: null,
        dateTo: null,
        warehouse: null
      },
      formRules: {
        dateFrom: [
          { validator: validateDateFrom, trigger: 'blur' }
        ],
        dateTo: [
          { validator: validateDateTo, trigger: 'blur' }
        ]
      },
      maxDays: 0,
    };
  },
  mounted() {
    shuttleFileService.getMaximumDays().then((response) => {
      this.maxDays = response.data;
    });
  },
  methods: {
    ...mapActions({
      countByDate: SHUTTLE_FILE_COUNT_BY_DATE,
      countUnitsToGenerate: SHUTTLE_FILE_COUNT_UNITS_TO_GENERATE,
      generate: SHUTTLE_FILE_GENERATE,
      shuttleFileSearch: SHUTTLE_FILE_SEARCH
    }),
    handleClose(done) {
      done();
    },
    handleOpen() {
      this.$nextTick(() => {
        this.resetForm();
      });
    },
    async overrideConfirmation() {
      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      const response = await this.countByDate(this.form).catch(() => {});
      if (response.data && response.data > 0) {
        loading.close();
        return this.$confirm(this.$t('routing.shuttleSetUp.confirmOverride'), {
          confirmButtonText: this.$t('routing.shuttleSetUp.overrideShuttleFile'),
          dangerouslyUseHTMLString: true
        }).catch(() => false);
      }
      loading.close();
      return true;
    },
    async unitCountConfirmation() {
      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      const response = await this.countUnitsToGenerate(this.form).catch(() => {});
      if (response.data && (response.data.pending > 0 || response.data.shuttledOrLoaded > 0)) {
        loading.close();
        const message = this.$t('routing.shuttleSetUp.pendingUnitsConfirmation').replace('[pendingUnits]', response.data.pending)
          .concat(this.$t('routing.shuttleSetUp.shuttledOrLoadedUnitsConfirmation').replace('[shuttledOrLoadedUnits]', response.data.shuttledOrLoaded))
          .concat(this.$t('routing.shuttleSetUp.continueConfirmation'));
        return this.$confirm(message, {
          dangerouslyUseHTMLString: true
        }).catch(() => false);
      }
      loading.close();
      return true;
    },
    async handleSubmit() {
      const valid = await this.$refs.form.validate().catch(() => {});
      if (valid) {
        Message.closeAll();
        if (await this.overrideConfirmation() && await this.unitCountConfirmation()) {
          this.generateShuttleFile();
        }
      }
    },
    async generateShuttleFile() {
      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      const response = await this.generate(this.form).catch(() => {});
      if (response) {
        this.resetForm();
        this.shuttleFileSearch({});
        this.$message.success(response.message);
        this.dialogVisible = false;
      }
      loading.close();
    },
    resetForm() {
      this.form.dateFrom = this.receivingDate;
      this.form.dateTo = this.receivingDate;
      this.form.warehouse = this.user.warehouseCode;
    }
  }
};
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
button#btn-generate-shuttle-file {
  margin-right: 10px;
}
</style>
